import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Auto Save Hook
 * 
 * Custom hook for intelligent automatic saving that saves user data transparently and efficiently with:
 * - Automatic periodic saving
 * - Immediate save on changes (with debounce)
 * - Multiple storage backends (localStorage, sessionStorage, indexedDB)
 * - Data compression
 * - Data encryption (basic)
 * - Version history
 * - Save status tracking
 * 
 * @module hooks/useAutoSave
 * @param {Object} data - Data to auto-save
 * @param {string} key - Unique key to identify the data
 * @param {Object} [options={}] - Auto-save configuration options
 * @param {number} [options.interval=30000] - Save interval in milliseconds (30 seconds)
 * @param {boolean} [options.immediate=false] - Whether to save immediately on changes
 * @param {string} [options.storage='localStorage'] - Storage type ('localStorage', 'sessionStorage', 'indexedDB')
 * @param {Function} [options.onSave] - Callback after save
 * @param {Function} [options.onError] - Callback on error
 * @param {boolean} [options.compress=false] - Whether to compress data
 * @param {boolean} [options.encrypt=false] - Whether to encrypt data (basic)
 * @param {number} [options.maxVersions=5] - Maximum number of versions to keep
 * @param {number} [options.debounceMs=1000] - Debounce delay for immediate saves in milliseconds
 * @returns {Object} Auto-save state and methods (saveStatus, lastSaved, versions, save, restore)
 */
export const useAutoSave = (data, key, options = {}) => {
  const { _t } = useTranslation();
  const {
    interval = 30000, // Save every 30 seconds by default
    immediate = false, // Immediate save on changes
    storage = 'localStorage', // Storage type (localStorage, sessionStorage, indexedDB)
    onSave = null, // Callback after save
    onError = null, // Callback on error
    compress = false, // Data compression
    encrypt = false, // Data encryption (basic)
    maxVersions = 5, // Maximum number of versions to keep
    debounceMs = 1000 // Debounce delay for immediate saves
  } = options;

  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null);
  const [versions, setVersions] = useState([]);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastDataRef = useRef(null);

  // Simple hashing function to detect changes
  const hashData = useCallback((obj) => {
    return JSON.stringify(obj).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
  }, []);

  // Simple compression function (base64)
  const compressData = useCallback((data) => {
    if (!compress) return data;
    try {
      return btoa(JSON.stringify(data));
    } catch (error) {
      return data;
    }
  }, [compress]);

  // Decompression function
  const decompressData = useCallback((data) => {
    if (!compress || typeof data !== 'string') return data;
    try {
      return JSON.parse(atob(data));
    } catch (error) {
      return data;
    }
  }, [compress]);

  // Basic encryption function (ROT13 + base64)
  const encryptData = useCallback((data) => {
    if (!encrypt) return data;
    try {
      const jsonStr = JSON.stringify(data);
      const rot13 = jsonStr.replace(/[a-zA-Z]/g, (c) => 
        String.fromCharCode((c <= 'Z' ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26)
      );
      return btoa(rot13);
    } catch (error) {
      return data;
    }
  }, [encrypt]);

  // Decryption function
  const decryptData = useCallback((data) => {
    if (!encrypt || typeof data !== 'string') return data;
    try {
      const rot13 = atob(data);
      const original = rot13.replace(/[a-zA-Z]/g, (c) => 
        String.fromCharCode((c <= 'Z' ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26)
      );
      return JSON.parse(original);
    } catch (error) {
      return data;
    }
  }, [encrypt]);

  // IndexedDB save function - MUST be defined before saveData to avoid TDZ error
  const saveToIndexedDB = useCallback((key, data) => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AutoSaveDB', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['autosaves'], 'readwrite');
        const store = transaction.objectStore('autosaves');
        
        const putRequest = store.put({ key, data, timestamp: Date.now() });
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('autosaves')) {
          const store = db.createObjectStore('autosaves', { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }, []);

  // Recovery function from IndexedDB - MUST be defined before loadData to avoid TDZ error
  const loadFromIndexedDB = useCallback((key) => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AutoSaveDB', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['autosaves'], 'readonly');
        const store = transaction.objectStore('autosaves');
        
        const getRequest = store.get(key);
        getRequest.onsuccess = () => {
          resolve(getRequest.result ? getRequest.result.data : null);
        };
        getRequest.onerror = () => reject(getRequest.error);
      };
    });
  }, []);

  // Delete function from IndexedDB - MUST be defined before clearSavedData to avoid TDZ error
  const clearFromIndexedDB = useCallback((key) => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AutoSaveDB', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['autosaves'], 'readwrite');
        const store = transaction.objectStore('autosaves');
        
        const deleteRequest = store.delete(key);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
      };
    });
  }, []);

  // Main save function
  const saveData = useCallback(async (dataToSave = data, force = false) => {
    if (!dataToSave || (!force && JSON.stringify(dataToSave) === JSON.stringify(lastDataRef.current))) {
      return; // No change, no save
    }

    setSaveStatus('saving');
    
    try {
      const timestamp = new Date().toISOString();
      const hash = hashData(dataToSave);
      
      // Data preparation with metadata
      const savePackage = {
        data: dataToSave,
        timestamp,
        hash,
        version: Date.now(),
        key
      };

      // Apply transformations
      let processedData = savePackage;
      if (compress) processedData.data = compressData(processedData.data);
      if (encrypt) processedData = encryptData(processedData);

      // Save based on storage type
      switch (storage) {
        case 'localStorage':
          localStorage.setItem(`autosave_${key}`, JSON.stringify(processedData));
          break;
        
        case 'sessionStorage':
          sessionStorage.setItem(`autosave_${key}`, JSON.stringify(processedData));
          break;
        
        case 'indexedDB':
          await saveToIndexedDB(key, processedData);
          break;
        
        default:
          throw new Error(`Storage type '${storage}' not supported`);
      }

      // Version management
      const newVersions = [...versions, { timestamp, hash, version: savePackage.version }]
        .slice(-maxVersions);
      setVersions(newVersions);

      // Update the state
      lastDataRef.current = dataToSave;
      setLastSaved(timestamp);
      setSaveStatus('saved');

      // Callback de success
      if (onSave) {
        onSave({
          timestamp,
          hash,
          version: savePackage.version,
          size: JSON.stringify(processedData).length
        });
      }

      // Back au status idle after 2 secondes
      setTimeout(() => setSaveStatus('idle'), 2000);

    } catch (error) {
      setSaveStatus('error');
      
      if (onError) {
        onError(error);
      }

      // Back to idle status after 5 seconds on error
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [data, key, storage, compress, encrypt, maxVersions, onSave, onError, hashData, compressData, encryptData, versions, saveToIndexedDB]);

  // Data recovery function
  const loadData = useCallback(async () => {
    try {
      let savedData = null;

      switch (storage) {
        case 'localStorage':
          savedData = localStorage.getItem(`autosave_${key}`);
          break;
        
        case 'sessionStorage':
          savedData = sessionStorage.getItem(`autosave_${key}`);
          break;
        
        case 'indexedDB':
          savedData = await loadFromIndexedDB(key);
          break;
        
        default:
          throw new Error(`Storage type '${storage}' not supported`);
      }

      if (!savedData) return null;

      // Parsing et decryption
      let parsedData = typeof savedData === 'string' ? JSON.parse(savedData) : savedData;
      if (encrypt) parsedData = decryptData(parsedData);
      if (compress && parsedData.data) parsedData.data = decompressData(parsedData.data);

      return parsedData;

    } catch (error) {
      return null;
    }
  }, [key, storage, encrypt, compress, decompressData, decryptData, loadFromIndexedDB]);

  // Saved data deletion function
  const clearSavedData = useCallback(async () => {
    try {
      switch (storage) {
        case 'localStorage':
          localStorage.removeItem(`autosave_${key}`);
          break;
        
        case 'sessionStorage':
          sessionStorage.removeItem(`autosave_${key}`);
          break;
        
        case 'indexedDB':
          await clearFromIndexedDB(key);
          break;
      }
      
      setLastSaved(null);
      setVersions([]);
      lastDataRef.current = null;
      
    } catch (error) { console.error(error); }
  }, [key, storage, clearFromIndexedDB]);

  // Manual save function
  const manualSave = useCallback(() => {
    saveData(data, true);
  }, [saveData, data]);

  // Effect for automatic save by interval
  useEffect(() => {
    if (interval > 0) {
      intervalRef.current = setInterval(() => {
        saveData();
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [interval, saveData]);

  // Effect for immediate save with debounce
  useEffect(() => {
    if (immediate && data) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        saveData();
      }, debounceMs);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [immediate, data, debounceMs, saveData]);

  // Cleanup on dismount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Hook API return
  return {
    // state
    saveStatus,
    lastSaved,
    versions,
    isAutoSaving: saveStatus === 'saving',
    hasUnsavedChanges: JSON.stringify(data) !== JSON.stringify(lastDataRef.current),
    
    // Actions
    manualSave,
    loadData,
    clearSavedData,
    
    // Information
    storageType: storage,
    autoSaveInterval: interval,
    lastSaveTime: lastSaved ? new Date(lastSaved) : null,
    
    // Statistics
    stats: {
      totalSaves: versions.length,
      storageSize: lastSaved ? JSON.stringify(data).length : 0,
      compressionEnabled: compress,
      encryptionEnabled: encrypt
    }
  };
};

export default useAutoSave;

